import { Alert, Button, Card, Divider } from "@blueprintjs/core";
import {
  DialogNewMediaFolder,
  DialogUploadMedia,
} from "@package/ui-apollo-components";
import { useToaster } from "@package/ui-components";
import {
  GetMediaDocument,
  GetMediaGridDocument,
  useDeleteMediaMutation,
  useGetMediaQuery,
} from "@package/ui-gql/index-data-access";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ITEM_GRID_PATH_EDIT,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { setItemGridSelectedItemId } from "../../../../store/slices/slice-item-grid";
import {
  setMediaViewType,
  setSelectedParentIdHierarchy,
} from "../../../../store/slices/slice-media";
import { useAppDispatch, useAppSelector } from "../../../../store/store";

export function MediaGridToolbar() {
  const { search } = useLocation();
  const { toaster } = useToaster();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const selectedItemId = useAppSelector(
    (state) => state.itemGrid.media.selectedItemId,
  );
  const selectedParentIdHierarchy = useAppSelector(
    (state) => state.media.selectedParentIdHierarchy,
  );
  const viewType = useAppSelector((state) => state.media.viewType);

  const [isDialogNewOpen, setIsDialogNewOpen] = useState<boolean>(false);
  const [isDialogUploadOpen, setIsDialogUploadOpen] = useState<boolean>(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState<boolean>(false);

  const { data: media, loading: mediaLoading } = useGetMediaQuery({
    variables: { id: selectedItemId ?? "" },
    skip: !selectedItemId,
  });
  const [deleteMedia] = useDeleteMediaMutation();

  return (
    <Card compact elevation={1}>
      <div className="flex flex-row grow">
        <h4 className="m-0 content-center bp6-heading">Media Library</h4>
        <div className="flex grow justify-end">
          <Button
            minimal
            intent="primary"
            text="Create Folder"
            icon="folder-new"
            onClick={() => setIsDialogNewOpen(true)}
          />
          <Button
            minimal
            intent="primary"
            text="Upload Files"
            icon="cloud-upload"
            onClick={() => setIsDialogUploadOpen(true)}
          />
          <Divider />
          <Button
            minimal
            className="mr-1 ml-2"
            title="Grid View"
            icon="list"
            active={viewType === "grid"}
            onClick={() => dispatch(setMediaViewType("grid"))}
          />
          <Button
            minimal
            className="mr-2"
            title="Content View"
            icon="layout-grid"
            active={viewType === "tile"}
            onClick={() => dispatch(setMediaViewType("tile"))}
          />
          <Divider />
          <Button
            minimal
            intent="warning"
            text="Edit"
            icon="edit"
            disabled={!selectedItemId || mediaLoading || media?.item?.isDir}
            onClick={() =>
              navigate(
                `/${SidebarEntryPath.media}/${ITEM_GRID_PATH_EDIT}/${selectedItemId}${search}`,
              )
            }
          />
          <Alert
            cancelButtonText="Cancel"
            canEscapeKeyCancel
            canOutsideClickCancel
            confirmButtonText="Delete"
            icon="trash"
            intent="danger"
            isOpen={isDeleteAlertOpen}
            onCancel={() => setIsDeleteAlertOpen(false)}
            onConfirm={() => {
              const toastId = toaster?.show({
                icon: "trash",
                intent: "none",
                message: `Deleting Media`,
                timeout: 10000,
              });

              deleteMedia({
                notifyOnNetworkStatusChange: true,
                refetchQueries: [GetMediaDocument, GetMediaGridDocument],
                variables: { id: selectedItemId ?? "" },
                onCompleted: () => {
                  dispatch(
                    setItemGridSelectedItemId({
                      path: SidebarEntryPath.media,
                      id: undefined,
                    }),
                  );
                  if (toastId) {
                    toaster?.dismiss(toastId);
                  }
                  toaster?.show({
                    icon: "tick",
                    intent: "success",
                    message: `Deleted Media`,
                    timeout: 3000,
                  });
                },
              });

              setIsDeleteAlertOpen(false);
            }}
          >
            <p>Are you sure you want to delete this resource?</p>
          </Alert>
          <Button
            minimal
            intent="danger"
            text="Delete"
            icon="trash"
            disabled={!selectedItemId}
            onClick={() => setIsDeleteAlertOpen(true)}
          />
          <Divider />
          <Button minimal intent="none" text="Export" icon="export" disabled />
        </div>
      </div>
      <DialogNewMediaFolder
        isOpen={isDialogNewOpen}
        selectedParentIdHierarchy={selectedParentIdHierarchy}
        onClose={() => setIsDialogNewOpen(false)}
        onCreated={(id) => {
          dispatch(
            setSelectedParentIdHierarchy([...selectedParentIdHierarchy, id]),
          );
        }}
        onError={() => {
          toaster?.show({
            message: "Failed creating new folder",
            icon: "error",
            intent: "danger",
            timeout: 3000,
          });
        }}
      />
      <DialogUploadMedia
        isOpen={isDialogUploadOpen}
        selectedParentIdHierarchy={selectedParentIdHierarchy}
        onClose={() => setIsDialogUploadOpen(false)}
      />
    </Card>
  );
}
